import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Box, Tooltip } from '@mui/material'
import MuiDrawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { CSSObject, styled, Theme } from '@mui/material/styles'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { useNextStep } from 'nextstepjs'

import { useAppSelector } from '#/app/store/store'
import { ERROR_REPORT, getModalById } from '#/features/modals'

import '../styles/styles.module.css'
import styles from '../styles/styles.module.css'

export const menuListTop = [
	{ title: 'Главная', link: '/', icon: '/svg/side-menu/home-page', activeList: [] },
	{ title: 'Дашборд', link: '/dashboard', icon: '/svg/side-menu/market', activeList: ['dashboard'] },
	{
		title: 'Оплата',
		link: '/subscription',
		icon: '/svg/side-menu/star',
		activeList: ['subscribe'],
	},
	{
		title: 'Настройки',
		link: '/account?scope=setting',
		icon: '/svg/side-menu/settings',
		activeList: ['account'],
	},
]

export const menuListMiddle = [
	{
		title: 'Чат-боты',
		link: '/chat-bot',
		icon: '/svg/side-menu/chat',
		activeList: ['chat-bot'],
	},
	{
		title: 'Изображения',
		link: '/images',
		icon: '/svg/side-menu/image',
		activeList: ['images'],
	},
	{
		title: 'Видео',
		link: '/videos',
		icon: '/svg/side-menu/video',
		activeList: ['videos'],
	},
	{
		title: 'Музыка',
		link: '/audio',
		icon: '/svg/side-menu/audio',
		activeList: ['audio'],
	},
	{
		title: 'Клонирование голоса',
		link: '/voice-cloning',
		icon: '/svg/side-menu/voice-cloning',
		activeList: ['voice-cloning'],
	},
]

const drawerWidth = 240

const DrawerHeader = styled('div')(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	padding: theme.spacing(0, 0),
	...theme.mixins.toolbar,
}))

const openedMixin = (theme: Theme): CSSObject => ({
	width: drawerWidth,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up('sm')]: {
		width: `calc(${theme.spacing(8)} + 10px)`,
	},
})

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
	width: drawerWidth,
	flexShrink: 0,
	position: 'absolute',
	whiteSpace: 'nowrap',
	boxSizing: 'border-box',
	...(open && {
		...openedMixin(theme),
		'& .MuiDrawer-paper': openedMixin(theme),
	}),
	...(!open && {
		...closedMixin(theme),
		'& .MuiDrawer-paper': closedMixin(theme),
	}),
}))

export const SideMenu = ({}) => {
	const { status } = useSession()
	const { pathname, asPath } = useRouter()
	const account_type = useAppSelector((state) => state.user.account_type)
	const { isNextStepVisible } = useNextStep()

	const error_report = getModalById(ERROR_REPORT)

	const [open, setOpen] = useState<'opened' | 'closed'>('closed')

	useEffect(() => {
		if (isNextStepVisible) setOpen('closed')
	}, [isNextStepVisible, setOpen])

	const filteredMenuListTop = useMemo(() => {
		return menuListTop.filter((item) => {
			if (item.title === 'Главная' || item.title === 'Оплата') {
				return ['business_host', 'regular'].includes(account_type)
			}

			return true
		})
	}, [account_type])

	const handleCloseDrawer = () => {
		if (!isNextStepVisible) setOpen('closed')
	}

	const handleToggleOpen = () => {
		if (!isNextStepVisible) {
			setOpen(open === 'opened' ? 'closed' : 'opened')
		}
	}

	return (
		<>
			{open === 'opened' && (
				<Box
					aria-hidden
					onClick={handleCloseDrawer}
					sx={{
						position: 'fixed',
						inset: 0,
						bgcolor: 'rgba(0, 0, 0, 0.5)',
						zIndex: 1199,
						transition: 'opacity 0.2s ease-in-out',
					}}
				/>
			)}
			<Drawer
				className={styles.drawer}
				PaperProps={{
					sx: {
						backgroundColor: '#151518',
						border: 'none',
						zIndex: isNextStepVisible ? 800 : 1200,
					},
				}}
				variant='permanent'
				anchor='left'
				open={open === 'opened'}
			>
				<DrawerHeader
					sx={{
						display: 'flex',
						flexDirection: 'column',
						padding: '12px 0px 0px',
						justifyContent: 'center',
					}}
				>
					<Box onClick={handleToggleOpen} width='100%' display={'flex'} alignItems={'center'} justifyContent='space-between'>
						<Image className={styles.logoImage} priority src='/logo.svg' height={26} width={31} alt='Error' />

						<Image
							className={`${styles.toggleArrow} ${open === 'closed' ? styles.toggleArrow_closed : styles.toggleArrow_open}`}
							src={'/arrow-left.svg'}
							width={20}
							height={13}
							alt={' '}
						/>
					</Box>
				</DrawerHeader>
				{status === 'authenticated' && (
					<Box display={'flex'} justifyContent={'space-between'} flexDirection={'column'} sx={{ height: '90%' }}>
						<Box>
							<List sx={{ padding: 0 }}>
								{filteredMenuListTop.map((el) => {
									return (
										<MenuItem
											key={el.title}
											open={open === 'opened'}
											pathname={asPath}
											title={el.title}
											link={el.link}
											icon={el.icon}
											activeList={el.activeList}
										/>
									)
								})}
								<Box sx={{ marginTop: 1.5 }}>
									{menuListMiddle.map((el) => {
										return (
											<MenuItem
												key={el.title}
												open={open === 'opened'}
												pathname={asPath}
												title={el.title}
												link={el.link}
												icon={el.icon}
												activeList={el.activeList}
											/>
										)
									})}
								</Box>
							</List>
							<Box sx={{ marginTop: 1.5 }}>
								<Box
									onClick={() => {
										error_report.setState(true)
									}}
								>
									<MenuItem
										open={open === 'opened'}
										pathname={pathname}
										title={'Поддержка'}
										link={null}
										icon={'/svg/side-menu/side-question'}
									/>
								</Box>
								<MenuItem
									open={open === 'opened'}
									pathname={pathname}
									title={'API-ключи'}
									link={'/api-keys'}
									icon={'/svg/side-menu/api-keys'}
								/>
							</Box>
						</Box>
					</Box>
				)}
			</Drawer>
		</>
	)
}

type MenuItemProps = {
	open: boolean
	pathname: string

	title: string
	link: string | null
	icon: string | React.ReactNode
	activeList?: string[]
	target?: string
}

export function MenuItem(props: MenuItemProps) {
	const isActive = useMemo(() => props.link === props.pathname || props.activeList?.some((el) => props.pathname.includes(el)), [props.pathname])

	return (
		<Tooltip
			title={props.title}
			placement='right'
			key={props.title}
			arrow
			componentsProps={{
				tooltip: {
					sx: {
						bgcolor: '#4B4B4B',
						color: '#7F7DF3',
						fontSize: 15,
						borderRadius: '10px',
						padding: '10px 13px',
						'& .MuiTooltip-arrow': {
							color: '#4B4B4B',
						},
						boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.04), 0px 4px 32px rgba(0, 0, 0, 0.16)',
					},
				},
			}}
		>
			<Link onClick={(e) => !props.link && e.preventDefault()} href={props.link || ''} target={props.target}>
				<ListItem
					disablePadding
					sx={{
						maxHeight: 50,
						padding: '0 5px',
						display: 'block',
						paddingBottom: 0,
						paddingTop: 0,
					}}
				>
					<ListItemButton
						sx={{
							minHeight: 40,
							justifyContent: props.open ? 'initial' : 'center',
							px: 0.8,
							display: 'flex',
						}}
						className={styles.listItemTransparent}
					>
						<ListItem
							component='div'
							sx={{
								backgroundColor: isActive ? 'rgba(130, 128, 255, 0.08)' : 'none',
								borderRadius: '10px',
								justifyContent: 'center',
							}}
						>
							<ListItemIcon
								sx={{
									minWidth: 0,
									mr: props.open ? 1 : 'auto',
									justifyContent: 'center',
								}}
							>
								{typeof props.icon === 'string' ? (
									<Image src={props.icon + (isActive ? '' : '-off') + '.svg'} width={19} height={19} alt={''} />
								) : (
									<div className={isActive ? styles.menuIcon_active : styles.menuIcon}>
										{props.icon}
									</div>
								)}
							</ListItemIcon>
							<ListItemText
								primary={props.title}
								sx={{
									opacity: props.open ? 1 : 0,
									'.MuiTypography-root': {
										fontSize: 16,
										color: isActive ? '#8280FF' : '#D4D4D4',
									},
								}}
							/>
						</ListItem>
					</ListItemButton>
				</ListItem>
			</Link>
		</Tooltip>
	)
}
